import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Clapperboard,
  ArrowRight,
  CheckCircle2,
  Film,
  Search,
  Network,
  Briefcase,
  ClipboardList,
  TrendingUp,
  GraduationCap,
  Repeat,
  Megaphone,
  Users,
  Wrench,
  MapPin,
  Gift,
  Quote,
  Menu,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { WaitlistForm } from "@/components/WaitlistForm";

const COVER =
  "https://customer-assets.emergentagent.com/job_filmind-hub/artifacts/xdc6xxcd_Lights%2C%20Camera%2C%20Take%20Action_Final.png";
const HERO_BG = "https://images.pexels.com/photos/13812458/pexels-photo-13812458.jpeg";
const AUTHOR =
  "https://customer-assets.emergentagent.com/job_filmind-hub/artifacts/idqgiqa1_Landing%20page_Blue%20Button%20Up.jpeg";
const LOGO = "/panthyon-logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const LEARN_TOPICS = [
  { icon: Film, title: "Understanding the Film Industry", desc: "How the business really works behind the camera." },
  { icon: Search, title: "Finding Opportunities", desc: "Where the jobs are and how to spot your way in." },
  { icon: Network, title: "Networking & Building Relationships", desc: "Turn connections into a lasting career." },
  { icon: Briefcase, title: "Landing Your First Job", desc: "Stand out and get hired on real productions." },
  { icon: ClipboardList, title: "Succeeding as a Production Assistant", desc: "Excel in the role that opens every door." },
  { icon: TrendingUp, title: "Building a Long-Term Career", desc: "Grow from PA to the role you're aiming for." },
];

const AUDIENCE = [
  { icon: ClipboardList, label: "Production Assistants" },
  { icon: GraduationCap, label: "Film Students" },
  { icon: Repeat, label: "Career Changers" },
  { icon: Clapperboard, label: "Emerging Filmmakers" },
  { icon: Megaphone, label: "Future Producers" },
  { icon: Users, label: "Anyone Looking to Break Into Film & TV" },
];

const STATS = [
  { value: "10+", label: "Years in Film & Television" },
  { value: "6,000+", label: "Community Members Served" },
  { value: "Major", label: "Television Productions" },
];

const PRODUCTIONS = ["The Chi", "Power Book IV: Force", "Empire", "Mo", "Filmscape Chicago"];

const KIT_ITEMS = [
  { icon: Briefcase, label: "Film Industry Career Resources" },
  { icon: Search, label: "Job Sites & Hiring Resources" },
  { icon: Network, label: "Film Festivals & Networking Opportunities" },
  { icon: GraduationCap, label: "Training & Education Programs" },
  { icon: Users, label: "Industry Organizations & Communities" },
  { icon: Wrench, label: "Career Development Tools" },
  { icon: MapPin, label: "Chicago Film Industry Resources" },
];

const TESTIMONIALS = [
  {
    quote:
      "Desmond's guidance turned my confusion into a clear plan. I landed my first PA gig within a month of attending his workshop.",
    name: "Jordan M.",
    role: "Production Assistant",
  },
  {
    quote:
      "Finally, a roadmap that speaks to people breaking in without connections. Practical, honest, and incredibly motivating.",
    name: "Priya S.",
    role: "Aspiring Producer",
  },
  {
    quote:
      "The networking resources alone are worth it. This is the mentor I wish I had when I started in the industry.",
    name: "Marcus T.",
    role: "Camera Operator",
  },
];

function Nav({ onJoin }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  const links = [
    ["What You'll Learn", "learn"],
    ["Who It's For", "audience"],
    ["About", "about"],
    ["Free Guide", "waitlist"],
  ];
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#060D18]/85 border-b border-[#C5A059]/15" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-12">
        <a href="#top" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <img
            src={LOGO}
            alt="Panthyon Media Group"
            className="h-10 sm:h-11 w-auto"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </a>
        <nav className="hidden md:flex items-center gap-7 lg:gap-8 shrink-0">
          {links.map(([label, id]) => (
            <a
              key={id}
              href={`#${id}`}
              className="text-sm text-[#94A3B8] hover:text-[#C5A059] transition-colors whitespace-nowrap"
            >
              {label}
            </a>
          ))}
          <Button
            onClick={onJoin}
            data-testid="nav-join-btn"
            className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full px-6 transition-all hover:-translate-y-0.5"
          >
            Join the Waitlist
          </Button>
        </nav>
        <button
          className="md:hidden text-white"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-btn"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-[#0C172A] border-t border-[#C5A059]/15 px-5 py-4 flex flex-col gap-4">
          {links.map(([label, id]) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={() => setOpen(false)}
              className="text-[#94A3B8] hover:text-[#C5A059]"
            >
              {label}
            </a>
          ))}
          <Button
            onClick={() => {
              setOpen(false);
              onJoin();
            }}
            className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full"
          >
            Join the Waitlist
          </Button>
        </div>
      )}
    </header>
  );
}

function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`relative py-24 sm:py-28 px-5 sm:px-8 ${className}`}>
      <div className="max-w-7xl mx-auto">{children}</div>
    </section>
  );
}

function Overline({ children }) {
  return (
    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#C5A059]">{children}</span>
  );
}

function SectionHeading({ overline, title, intro, center = true }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      className={`${center ? "text-center mx-auto" : ""} max-w-2xl mb-14`}
    >
      <Overline>{overline}</Overline>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-3">{title}</h2>
      {intro && <p className="text-[#94A3B8] mt-5 text-base leading-relaxed">{intro}</p>}
    </motion.div>
  );
}

export default function LandingPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => setDialogOpen(true);

  return (
    <div id="top" className="bg-[#060D18] text-white overflow-x-hidden">
      <Nav onJoin={openDialog} />

      {/* HERO — BOOK FIRST */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16 grain">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Film set" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#060D18] via-[#060D18]/90 to-[#060D18]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060D18] via-transparent to-[#060D18]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center w-full">
          <motion.div initial="hidden" animate="show" className="lg:col-span-7 text-center lg:text-left">
            <motion.div variants={fadeUp} custom={0}>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#E50914]/15 border border-[#E50914]/40 text-[#E50914] text-xs font-bold uppercase tracking-widest px-4 py-1.5">
                <span className="h-2 w-2 rounded-full bg-[#E50914] animate-pulse" /> Coming Soon
              </span>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              custom={1}
              className="font-display text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight mt-5 leading-[1.05]"
            >
              Lights, Camera,
              <br />
              <span className="text-gradient-gold">Take Action</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="text-base sm:text-lg text-[#94A3B8] mt-6 max-w-xl mx-auto lg:mx-0 leading-relaxed"
            >
              Your guide to breaking into film, getting hired, and building a career.
            </motion.p>
            <motion.div
              variants={fadeUp}
              custom={3}
              className="mt-6 flex items-start gap-3 max-w-xl mx-auto lg:mx-0 rounded-xl border border-[#C5A059]/20 bg-[#0C172A]/70 backdrop-blur-md p-4 text-left"
            >
              <Gift className="h-5 w-5 text-[#C5A059] shrink-0 mt-0.5" />
              <p className="text-sm text-[#CBD5E1]">
                Join the waitlist and receive a <span className="text-white font-semibold">FREE Film Industry Resource Guide</span> —
                packed with industry resources, job sites, networking opportunities, and career tools.
              </p>
            </motion.div>
            <motion.div
              variants={fadeUp}
              custom={4}
              className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Button
                onClick={openDialog}
                data-testid="hero-join-btn"
                className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full px-8 h-13 py-6 text-base transition-all hover:-translate-y-1"
              >
                Join the Waitlist <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a href="#learn">
                <Button
                  variant="outline"
                  data-testid="hero-learn-btn"
                  className="border-[#C5A059]/40 bg-transparent text-white hover:bg-[#C5A059]/10 hover:text-white rounded-full px-8 h-13 py-6 text-base w-full"
                >
                  Learn More
                </Button>
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="lg:col-span-5 flex justify-center"
          >
            <div className="relative">
              <div className="absolute -inset-8 spotlight blur-2xl" />
              <img
                src={COVER}
                alt="Lights, Camera, Take Action book cover"
                className="relative w-72 sm:w-[22rem] rounded-lg shadow-2xl shadow-[#C5A059]/20 ring-1 ring-[#C5A059]/20"
                data-testid="hero-book-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2 — WHAT YOU'LL LEARN */}
      <Section id="learn">
        <SectionHeading
          overline="What You'll Learn"
          title="Everything You Need to Launch Your Film Career"
          intro="Whether you're a film student, aspiring Production Assistant, career changer, or emerging filmmaker, this book provides practical guidance for navigating the film and television industry."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {LEARN_TOPICS.map(({ icon: Icon, title, desc }, i) => (
            <motion.div
              key={title}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="group rounded-2xl border border-[#C5A059]/15 bg-[#0C172A] p-7 hover:border-[#C5A059]/40 hover:-translate-y-1 transition-all"
            >
              <span className="h-12 w-12 rounded-xl bg-[#12213D] border border-[#C5A059]/20 flex items-center justify-center group-hover:bg-[#C5A059]/10 transition-colors">
                <Icon className="h-6 w-6 text-[#C5A059]" />
              </span>
              <h3 className="font-display text-xl text-white mt-5">{title}</h3>
              <p className="text-[#94A3B8] text-sm mt-2 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SECTION 3 — WHO THIS BOOK IS FOR */}
      <Section id="audience" className="bg-[#0C172A]">
        <SectionHeading overline="Who This Book Is For" title="Built for Aspiring Film Professionals" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {AUDIENCE.map(({ icon: Icon, label }, i) => (
            <motion.div
              key={label}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="group rounded-2xl border border-[#C5A059]/15 bg-[#060D18] p-7 flex items-center gap-4 hover:border-[#C5A059]/40 hover:-translate-y-1 transition-all"
            >
              <span className="h-12 w-12 rounded-xl bg-[#12213D] border border-[#C5A059]/20 flex items-center justify-center shrink-0 group-hover:bg-[#C5A059]/10 transition-colors">
                <Icon className="h-6 w-6 text-[#C5A059]" />
              </span>
              <span className="font-display text-lg text-white leading-snug">{label}</span>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SECTION 4 — ABOUT THE AUTHOR */}
      <Section id="about">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="md:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-3 border border-[#C5A059]/30 rounded-2xl translate-x-4 translate-y-4" />
              <img
                src={AUTHOR}
                alt="Desmond Huey"
                className="relative rounded-2xl w-full object-cover aspect-[4/5]"
                data-testid="author-image"
              />
            </div>
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="md:col-span-7"
          >
            <Overline>About the Author</Overline>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-3">Meet Desmond Huey</h2>
            <p className="text-[#94A3B8] mt-5 text-lg leading-relaxed">
              Desmond Huey is an award-winning filmmaker, actor, educator, producer, and Assistant Location
              Manager with more than a decade of experience in film and television.
            </p>
            <p className="text-[#94A3B8] mt-4 text-lg leading-relaxed">
              His credits include{" "}
              <span className="text-white font-medium">The Chi, Power Book IV: Force, Empire, Mo</span>,
              and numerous educational programs that have helped aspiring film professionals launch
              their careers.
            </p>
            <a href="#waitlist" className="inline-block mt-8">
              <Button
                data-testid="about-cta-btn"
                className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full px-7 h-12 transition-all hover:-translate-y-0.5"
              >
                Join the Waitlist <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </motion.div>
        </div>
      </Section>

      {/* SECTION 5 — INDUSTRY EXPERIENCE */}
      <section className="py-24 sm:py-28 bg-[#0C172A] border-y border-[#C5A059]/15 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHeading overline="Track Record" title="Industry Experience" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {STATS.map(({ value, label }, i) => (
              <motion.div
                key={label}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                <div className="font-display text-5xl sm:text-6xl font-black text-gradient-gold">{value}</div>
                <div className="text-[#94A3B8] mt-3 text-sm uppercase tracking-[0.15em]">{label}</div>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8] mt-16 mb-8">
            Credits &amp; Featured Productions
          </p>
          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-5">
            {PRODUCTIONS.map((label) => (
              <div
                key={label}
                className="px-5 py-3 rounded-lg border border-[#C5A059]/15 bg-[#060D18] text-[#94A3B8] hover:text-[#C5A059] hover:border-[#C5A059]/40 transition-colors"
              >
                <span className="font-display text-base sm:text-xl whitespace-nowrap tracking-wide">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — FREE BONUS RESOURCE + WAITLIST FORM */}
      <Section id="waitlist">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/40 text-[#C5A059] text-xs font-bold uppercase tracking-widest px-4 py-1.5">
              <Gift className="h-3.5 w-3.5" /> Included Free With the Waitlist
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-4">
              Free Film Industry <span className="text-gradient-gold">Resource Guide</span>
            </h2>
            <p className="text-white mt-3 text-lg font-medium">
              Join the waitlist and receive this bonus resource immediately.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-8">
              {KIT_ITEMS.map(({ label }) => (
                <li key={label} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-5 w-5 text-[#C5A059] shrink-0 mt-0.5" />
                  <span className="text-[#CBD5E1] text-sm">{label}</span>
                </li>
              ))}
            </ul>
            <p className="text-[#94A3B8] mt-7 text-sm leading-relaxed">
              Designed to help aspiring film professionals discover opportunities, build
              relationships, and navigate the industry with confidence.
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="rounded-2xl border border-[#C5A059]/20 bg-[#0C172A] p-7 sm:p-9 shadow-xl"
          >
            <h3 className="font-display text-2xl text-white mb-1">Get the Resource Guide</h3>
            <p className="text-sm text-[#94A3B8] mb-6">
              Join the waitlist for <span className="text-[#C5A059]">Lights, Camera, Take Action</span> and
              your free guide is delivered right away.
            </p>
            <WaitlistForm />
          </motion.div>
        </div>
      </Section>

      {/* SECTION 7 — TESTIMONIALS */}
      <Section id="reviews" className="bg-[#0C172A]">
        <SectionHeading overline="What People Are Saying" title="What Students & Professionals Say" />
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              variants={fadeUp}
              custom={i}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="rounded-2xl border border-[#C5A059]/15 bg-[#060D18]/70 backdrop-blur-md p-7 flex flex-col"
            >
              <Quote className="h-8 w-8 text-[#C5A059]/60" />
              <p className="text-[#E2E8F0] mt-4 leading-relaxed flex-1">"{t.quote}"</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-[#12213D] border border-[#C5A059]/30 flex items-center justify-center font-display text-[#C5A059]">
                  {t.name[0]}
                </div>
                <div>
                  <div className="text-white font-medium text-sm">{t.name}</div>
                  <div className="text-[#94A3B8] text-xs">{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <section className="relative py-32 px-5 sm:px-8 text-center grain overflow-hidden">
        <div className="absolute inset-0 spotlight" />
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative max-w-3xl mx-auto"
        >
          <Overline>The Release Is Coming</Overline>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-4 leading-tight">
            Ready to <span className="text-gradient-gold">Take Action?</span>
          </h2>
          <p className="text-[#94A3B8] mt-6 text-lg max-w-xl mx-auto">
            Join the waitlist today and receive the Free Film Industry Resource Guide while you wait for
            the release of Lights, Camera, Take Action.
          </p>
          <div className="mt-10">
            <Button
              onClick={openDialog}
              data-testid="final-cta-btn"
              className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full px-10 h-14 text-lg transition-all hover:-translate-y-1"
            >
              Join the Waitlist <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#C5A059]/15 py-10 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img
              src={LOGO}
              alt="Panthyon Media Group"
              className="h-10 w-auto"
              style={{ filter: "brightness(0) invert(1)" }}
            />
          </div>
          <p className="text-sm text-[#64748B]">
            © {new Date().getFullYear()} Desmond Huey. All rights reserved.
          </p>
          <a href="/admin" className="text-xs text-[#64748B] hover:text-[#C5A059]" data-testid="admin-link">
            Admin
          </a>
        </div>
      </footer>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0C172A] border-[#C5A059]/20 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Join the Waitlist</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Get early access to the book — plus your Free Film Industry Resource Guide, delivered right away.
            </DialogDescription>
          </DialogHeader>
          <WaitlistForm compact onSuccess={() => {}} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
